import { useMemo, useCallback } from "react";
import { observer } from "mobx-react";
import {
  useSimpleRegistrationContext,
  AlertTypeKey,
} from "pages/SimpleRegistrationsPage";
import { useApi } from "providers/ApiProvider";
import { useSnackbarContext } from "~/SnackbarProvider_v2";
import { AlertModal } from "components/Modal/AlertModal";
import Icon from "components/Icon";
import { SimpleRegistrationsCustomerListModal } from "modals/SimpleRegistrationsCustomerListModal";
import { DialButton } from "components/SimpleRegistrations/DialButton";
import { useHiddenFunction } from "hooks/useHiddenFunction";
import { Box } from "./RightSection.styled";
import { ApiException } from "~/core/apiClient";
import { useClinic } from "~/providers/ClinicProvider";
import VisitPurposeModal from "~/components/VisitPurposeModal";
import { ClinicConfig } from "~/enums/common";
import { ConfirmPhoneNumberModal } from "~/modals/ComfirmPhoneNumber_v2";
import { CreateNewRegistrationModal } from "modals/CreateNewRegistrationModal";
import { formatBirthdayWithGender } from "~/utils/formatUtils";

interface RightSectionProps {
  setOpenCompleteModal: (open: boolean) => void;
  onSaveNewRegistration: () => Promise<void>;
}

const ALERT_MESSAGES: Record<AlertTypeKey, JSX.Element> = {
  CUSTOMER_LIST_OVER: (
    <>
      추가 정보 확인이 필요합니다.
      <br />
      데스크에 문의 부탁드립니다.
    </>
  ),
  EXIST_REGISTRATION: (
    <>
      이미 접수된 고객입니다.
      <br />
      데스크에 문의 부탁드립니다.
    </>
  ),
};

const MAX_PHONE_LENGTH = 11;
const MAX_CUSTOMER_LIST_LENGTH = 3;
const DELAY_TIME = 1000;

export const RightSection = observer(
  ({ setOpenCompleteModal, onSaveNewRegistration }: RightSectionProps) => {
    const {
      phoneNumber,
      setPhoneNumber,
      setLoading,
      setShowNewCustomerText,
      setCustomers,
      setName,
      setBirthday,
      reset,
      currentIdx,
      setCurrentIdx,
      visitPurpose,
      setVisitPurpose,
      setCustomerId,
      setAcquisitionChannelsIds,
      alertState,
      setAlertState,
      openCustomerListModal,
      setOpenCustomerListModal,
      openVisitPurposeModal,
      setOpenVisitPurposeModal,
      selectedCustomer,
      setSelectedCustomer,
      confirmPhoneNumber,
      setConfirmPhoneNumber,
      openCreateNewRegistrationModal,
      setOpenCreateNewRegistrationModal,
    } = useSimpleRegistrationContext();
    const { customersApi, registrationApi } = useApi();
    const snackbar = useSnackbarContext();
    const { clinicConfig } = useClinic();

    useHiddenFunction(phoneNumber);

    const useRegistrationPurpose = useMemo(() => {
      if (!clinicConfig || clinicConfig.length === 0) return false;

      const config = clinicConfig.find(
        (item: { codeName: string }) =>
          item.codeName === ClinicConfig.useRegistrationPurpose
      );

      return config ? JSON.parse(config.codeValue || "false") : false;
    }, [clinicConfig]);

    const displayPhoneNumber = useMemo(() => {
      const value = phoneNumber
        .substring(0, MAX_PHONE_LENGTH)
        .padEnd(MAX_PHONE_LENGTH, "0");
      if (phoneNumber.length < 2) {
        return value.substring(0, 1) + "1" + value.substring(2);
      }
      return value;
    }, [phoneNumber]);

    const handleReset = useCallback(() => {
      reset();
    }, [reset]);

    const closeAlert = useCallback(() => {
      setAlertState({ open: false, type: null });
      setLoading(false);
      handleReset();
    }, [setAlertState, setLoading, handleReset]);

    const showAlert = useCallback(
      (type: AlertTypeKey) => {
        setAlertState({ open: true, type });
        setCurrentIdx(3);
      },
      [setAlertState, setCurrentIdx]
    );

    const onChangePhoneNumber = useCallback(
      (value: number) => {
        if (phoneNumber.length < MAX_PHONE_LENGTH) {
          setPhoneNumber((number: string) => number + value);
          setCurrentIdx(currentIdx + 1);
        }
      },
      [phoneNumber.length, currentIdx, setPhoneNumber, setCurrentIdx]
    );

    const handleBackspace = useCallback(() => {
      if (phoneNumber.length !== 0) {
        setPhoneNumber((number: string) => number.slice(0, number.length - 1));
        setCurrentIdx(currentIdx - 1);
      }
    }, [phoneNumber.length, currentIdx, setPhoneNumber, setCurrentIdx]);

    const newCustomerFlow = useCallback(
      (existUser: boolean) => {
        setLoading(true);
        if (!existUser) {
          setShowNewCustomerText("신규 고객 접수");
        }
        setTimeout(() => {
          setLoading(false);
          setOpenCreateNewRegistrationModal(true);
          setCurrentIdx(3);
        }, DELAY_TIME);
      },
      [
        setLoading,
        setShowNewCustomerText,
        setOpenCreateNewRegistrationModal,
        setCurrentIdx,
      ]
    );

    const establishedCustomerFlow = useCallback(
      (customerList: any[]) => {
        setCustomers(customerList);
        setOpenCustomerListModal(true);
        setCurrentIdx(3);
      },
      [setCustomers, setOpenCustomerListModal, setCurrentIdx]
    );

    const onRegistrationRequest = useCallback(
      async (customer: any) => {
        try {
          setLoading(true);
          const requestParams: Record<string, any> = {
            customerId: customer.customerId,
          };

          if (
            useRegistrationPurpose &&
            visitPurpose &&
            visitPurpose.length > 0
          ) {
            requestParams.purpose = visitPurpose
              .map((item) => item.name)
              .join(",");
          }

          await registrationApi.createRegistrationRequests(requestParams);
          setName(customer.name);
          setOpenCompleteModal(true);
        } catch (e) {
          handleReset();
          const error = e as ApiException;
          if (
            error.code === 400 &&
            error.message === "Duplicated Registration Request"
          ) {
            showAlert("EXIST_REGISTRATION");
            return;
          }
          snackbar.alert("실패했습니다.");
        } finally {
          setLoading(false);
        }
      },
      [
        useRegistrationPurpose,
        visitPurpose,
        registrationApi,
        setName,
        setOpenCompleteModal,
        handleReset,
        showAlert,
        snackbar,
        setLoading,
      ]
    );

    const handleVisitPurposeModalClose = useCallback(
      (selected?: boolean) => {
        setOpenVisitPurposeModal(false);

        if (selected && selectedCustomer) {
          onRegistrationRequest(selectedCustomer);
        } else {
          setVisitPurpose([]);
        }
      },
      [
        selectedCustomer,
        onRegistrationRequest,
        setOpenVisitPurposeModal,
        setVisitPurpose,
      ]
    );

    const handleCustomerListModalClose = useCallback(
      (value?: string, customer?: any) => {
        setOpenCustomerListModal(false);

        if (customer) {
          // 신규 방문 고객 처리
          if (customer.visitType === "NEW") {
            setCustomerId(customer.customerId);
            setName(customer.name);

            if (customer.acquisitionChannel) {
              const acquisitionChannelsIds = customer.acquisitionChannel
                .split(",")
                .map(Number);
              setAcquisitionChannelsIds(acquisitionChannelsIds);
            }

            const formattedBirthday = formatBirthdayWithGender(
              customer.birthday,
              customer.sex
            );
            setBirthday(formattedBirthday);

            newCustomerFlow(true);
            return;
          }

          // 이미 접수된 고객 처리
          if (customer.registrationToday === true) {
            setLoading(false);
            showAlert("EXIST_REGISTRATION");
            return;
          }
        }

        // 고객 선택에 따른 처리
        if (value === "registration") {
          if (useRegistrationPurpose) {
            setSelectedCustomer(customer || null);
            setVisitPurpose([]);
            setOpenVisitPurposeModal(true);
          } else if (customer) {
            onRegistrationRequest(customer);
          }
        } else if (value === "registration_new") {
          newCustomerFlow(false);
        } else {
          handleReset();
        }
      },
      [
        useRegistrationPurpose,
        setOpenCustomerListModal,
        setCustomerId,
        setName,
        setBirthday,
        newCustomerFlow,
        setLoading,
        showAlert,
        setSelectedCustomer,
        setVisitPurpose,
        setOpenVisitPurposeModal,
        onRegistrationRequest,
        handleReset,
      ]
    );

    const handleConfirmPhoneNumberClose = useCallback(
      (feedback?: boolean) => {
        setConfirmPhoneNumber(false);

        if (feedback) {
          newCustomerFlow(false);
        } else {
          handleReset();
        }
      },
      [setConfirmPhoneNumber, newCustomerFlow, handleReset]
    );

    const handleNewRegistrationComplete = useCallback(() => {
      setOpenCreateNewRegistrationModal(false);
      onSaveNewRegistration();
    }, [setOpenCreateNewRegistrationModal, onSaveNewRegistration]);

    const handleNewRegistrationCancel = useCallback(() => {
      setOpenCreateNewRegistrationModal(false);
      handleReset();
    }, [setOpenCreateNewRegistrationModal, handleReset]);

    const onClickNext = useCallback(async () => {
      try {
        setLoading(true);
        const res = await customersApi.getRegistrationCheck(phoneNumber);
        const customerList = res.data.data;

        if (customerList.length > 0) {
          if (customerList.length > MAX_CUSTOMER_LIST_LENGTH) {
            showAlert("CUSTOMER_LIST_OVER");
            return;
          }
          establishedCustomerFlow(customerList);
        } else {
          setConfirmPhoneNumber(true);
        }
      } catch (error) {
        snackbar.alert("고객 정보를 불러오는데 실패했습니다.");
        handleReset();
      } finally {
        setLoading(false);
      }
    }, [
      phoneNumber,
      setLoading,
      customersApi,
      showAlert,
      establishedCustomerFlow,
      setConfirmPhoneNumber,
      snackbar,
      handleReset,
    ]);

    return (
      <>
        {/* 알림 모달 */}
        {alertState.open && alertState.type && (
          <AlertModal
            body={
              <div style={{ fontWeight: "bold" }}>
                <div style={{ fontSize: "2vw", marginBottom: "5%" }}>안내</div>
                <div>{ALERT_MESSAGES[alertState.type]}</div>
              </div>
            }
            onClose={closeAlert}
            open={alertState.open}
          />
        )}
        {/* 전화번호 확인 모달 */}
        {confirmPhoneNumber && (
          <ConfirmPhoneNumberModal
            open={confirmPhoneNumber}
            onClose={handleConfirmPhoneNumberClose}
          />
        )}
        {/* 고객 목록 모달 */}
        {openCustomerListModal && (
          <SimpleRegistrationsCustomerListModal
            open={openCustomerListModal}
            onClose={handleCustomerListModalClose}
          />
        )}
        {/* 방문 목적 모달 */}
        {openVisitPurposeModal && (
          <VisitPurposeModal
            open={openVisitPurposeModal}
            onClose={handleVisitPurposeModalClose}
          />
        )}
        {/* 신규 접수 모달 */}
        {openCreateNewRegistrationModal && (
          <CreateNewRegistrationModal
            open={openCreateNewRegistrationModal}
            onClose={(value) => {
              if (value) {
                handleNewRegistrationComplete();
              } else {
                handleNewRegistrationCancel();
              }
            }}
          />
        )}
        <Box className="right-wrapper">
          <Box className="display-wrapper">
            <Box className="display-phone-number">
              {displayPhoneNumber
                .split("")
                .map((char: string, index: number) => (
                  <span
                    key={index}
                    className={
                      index >= currentIdx ? "place-holder" : "display-text"
                    }
                  >
                    {char}
                    {(index === 2 || index === 6) && "-"}
                  </span>
                ))}
            </Box>
          </Box>
          <Box className="dial-container">
            <Box className="dial-column">
              {[1, 2, 3].map((num) => (
                <DialButton
                  key={num}
                  text={num}
                  onClick={() => onChangePhoneNumber(num)}
                />
              ))}
            </Box>
            <Box className="dial-column">
              {[4, 5, 6].map((num) => (
                <DialButton
                  key={num}
                  text={num}
                  onClick={() => onChangePhoneNumber(num)}
                />
              ))}
            </Box>
            <Box className="dial-column">
              {[7, 8, 9].map((num) => (
                <DialButton
                  key={num}
                  text={num}
                  onClick={() => onChangePhoneNumber(num)}
                />
              ))}
            </Box>
            <Box className="dial-column">
              <DialButton
                text={
                  <Box className="icon-wrapper">
                    <Icon variant="backspace" />
                  </Box>
                }
                onClick={handleBackspace}
              />
              <DialButton text={0} onClick={() => onChangePhoneNumber(0)} />
              <DialButton
                text="다음"
                className={`next-button ${
                  phoneNumber.length >= MAX_PHONE_LENGTH
                    ? "able-button"
                    : "disable-button"
                }`}
                disabled={phoneNumber.length < MAX_PHONE_LENGTH}
                onClick={onClickNext}
              />
            </Box>
          </Box>
        </Box>
      </>
    );
  }
);
